
import { Box, LinearProgress, Link, Paper, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import useAuth from "../../context/AuthProvider";
import { EmailReportModuleModel } from '../../models/EmailReportModuleModel';
import { apiClient } from "../../helper/api";

const initValue: EmailReportModuleModel = {
    emailReportId: 0,
    customerName: "",
    templateName: "",
    emailSent: false,
    stateName: "",
    emailSendDate: "",
    blob_Address: "",
    content: ""
};

const emailReports: EmailReportModuleModel[] = [
    { ...initValue },
];

const borderColor = '#e2e8f0';
const headerBg = '#fafbfc';
const textPrimary = '#0f172a';
const textMuted = '#64748b';

// Format comma-separated values with space after comma so long lists wrap to next line
const formatCommaSeparated = (val?: string | null): string | undefined => {
    if (val == null || val === '') return undefined;
    return val.split(',').map((s) => s.trim()).filter(Boolean).join(', ');
};

// Helper function to strip HTML tags and decode HTML entities
const decodeHtmlContent = (html: string): string => {
    if (!html) return '';
    let text = html;
    text = text.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
    text = text.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
    text = text.replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '');
    text = text.replace(/<!--[\s\S]*?-->/g, '');
    text = text.replace(/\/\*[\s\S]*?\*\//g, '');
    text = text.replace(/<[^>]+>/g, '');
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value.replace(/\s+/g, ' ').trim();
};

const DetailRow = ({ label, value }: { label: string; value?: string | null }) => (
    <TableRow sx={{ '&:last-child td': { borderBottom: 0 } }}>
        <TableCell
            component="th"
            scope="row"
            sx={{
                width: '20%',
                fontWeight: 500,
                color: textMuted,
                backgroundColor: headerBg,
                borderBottom: `1px solid ${borderColor}`,
                py: 1.5,
                px: 2,
                whiteSpace: 'nowrap',
            }}
        >
            {label}
        </TableCell>
        <TableCell
            sx={{
                borderBottom: `1px solid ${borderColor}`,
                py: 1.5,
                px: 2,
                color: textPrimary,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
            }}
        >
            {value ?? '—'}
        </TableCell>
    </TableRow>
);

export const EmailReportsDetails = () => {
    const [loading, setloading] = useState(false);
    const [EmailReportModuleDetails, setEmailReportModule] = useState(emailReports);
    const { user } = useAuth();
    const token = user?.token;
    const params = useParams<{ emailReportId: string }>();

    const fetchData = useCallback(() => {
        setloading(true);
        apiClient(`/EmailReports/GetEmailReportsDetails?EmailReportId=${params.emailReportId}`,
            {
                method: "GET",
                headers: new Headers({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                })
            })
            .then((responseJson) => {
                setEmailReportModule(responseJson.data ?? emailReports);
            })
            .finally(() => {
                setloading(false);
            });
    }, [params.emailReportId, token]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const detail = EmailReportModuleDetails[0];
    const hasAttachments = Array.isArray(EmailReportModuleDetails) && EmailReportModuleDetails.some((e) => e?.documentName || e?.blob_Address);

    return (
        <Box sx={{ width: '100%', pb: 2 }}>
            {loading && (
                <LinearProgress
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        zIndex: 9999,
                        backgroundColor: '#132D3D',
                    }}
                />
            )}
            {/* Page header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    py: 1.5,
                    borderBottom: `1px solid ${borderColor}`,
                    backgroundColor: headerBg,
                    minHeight: 56,
                    mb: 2,
                }}
            >
                <Typography component="h1" sx={{ fontWeight: 600, fontSize: '1rem', color: textPrimary }}>
                    Email Report Details
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 2 }}>
                {/* Email Details card */}
                <Paper
                    elevation={0}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        borderRadius: '8px',
                        border: `1px solid ${borderColor}`,
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            fontWeight: 600,
                            fontSize: '0.9375rem',
                            color: textPrimary,
                            px: 2,
                            py: 1.5,
                            borderBottom: `1px solid ${borderColor}`,
                            backgroundColor: '#fff',
                        }}
                    >
                        Email Details
                    </Box>
                    <Table size="small">
                        <TableBody>
                            <DetailRow label="Template Name" value={detail?.templateName} />
                            <DetailRow label="Email Subject" value={detail?.subject?.toString()} />
                            <DetailRow label="CC" value={formatCommaSeparated(detail?.cc?.toString())} />
                            <DetailRow label="BCC" value={formatCommaSeparated(detail?.bcc?.toString())} />
                            <DetailRow label="To" value={formatCommaSeparated(detail?.mailSentTo?.toString())} />
                            <DetailRow label="Customer" value={detail?.customerName?.toString()} />
                        </TableBody>
                    </Table>
                </Paper>

                {/* Attachments card */}
                <Paper
                    elevation={0}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        borderRadius: '8px',
                        border: `1px solid ${borderColor}`,
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            fontWeight: 600,
                            fontSize: '0.9375rem',
                            color: textPrimary,
                            px: 2,
                            py: 1.5,
                            borderBottom: `1px solid ${borderColor}`,
                            backgroundColor: '#fff',
                        }}
                    >
                        Attachments
                    </Box>
                    <Box sx={{ px: 2, py: 1.5 }}>
                        {hasAttachments ? (
                            <Box component="ul" sx={{ m: 0, pl: 2.5, color: textPrimary }}>
                                {EmailReportModuleDetails.map((email, key) =>
                                    (email?.documentName || email?.blob_Address) ? (
                                        <Box component="li" key={key} sx={{ py: 0.5 }}>
                                            <Link
                                                href={email.blob_Address}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{
                                                    color: '#2099d2',
                                                    textDecoration: 'none',
                                                    fontSize: '0.875rem',
                                                    '&:hover': { textDecoration: 'underline' },
                                                    maxWidth: '100%',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: 'block',
                                                }}
                                            >
                                                {email.documentName || 'Attachment'}
                                            </Link>
                                        </Box>
                                    ) : null
                                )}
                            </Box>
                        ) : (
                            <Typography variant="body2" sx={{ color: textMuted }}>
                                No attachments
                            </Typography>
                        )}
                    </Box>
                </Paper>
            </Box>

            {/* Content card */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: '8px',
                    border: `1px solid ${borderColor}`,
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        fontWeight: 600,
                        fontSize: '0.9375rem',
                        color: textPrimary,
                        px: 2,
                        py: 1.5,
                        borderBottom: `1px solid ${borderColor}`,
                        backgroundColor: '#fff',
                    }}
                >
                    Content
                </Box>
                <Box
                    sx={{
                        px: 2,
                        py: 1.5,
                        color: textPrimary,
                        fontSize: '0.875rem',
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                    }}
                >
                    {typeof detail?.content === 'string'
                        ? decodeHtmlContent(detail.content)
                        : '—'}
                </Box>
            </Paper>
        </Box>
    );
};
